/**
 * The Undo toast — the one presentation of every Undo in the app: Capture's
 * Save, the Ledger's and Settings' deletes, and Sounding's Save. A floating
 * band, a plain message, an Undo and a Dismiss. Its five-second life is not
 * kept here: `useUndoWindow` owns the lifecycle, so every toast closes on the
 * same clock and none can be left standing (SPEC.md §3.3, §7).
 *
 * Undo is honest — the owning view reverses through the repo (a tombstone, or
 * `repo.restoreRows`, INV-5), so the affordance always does what it says (D-7).
 * While that reversal is in flight the button is marked busy rather than hidden:
 * the toast stays up until the reversal commits.
 *
 * Two anchors. `bar` clears the mobile action bar and sits lower on desktop,
 * where there is no bottom bar; `action` rides the same safe-area offset as
 * Capture's Save, so the toast occupies that button's footprint on phones.
 *
 * An `action` toast stays over Capture's own pane on wide screens
 * (`.toast-action`), where it covers Save as it does on a phone; centred on the
 * whole window it straddled both panes and half-covered Save's label.
 *
 * Portalled to `document.body`, like Sheet: on tablet and desktop Capture shares
 * the screen with Home, and a toast rendered inside Capture's pane sits in that
 * pane's stacking context, under Home, where Undo cannot be pressed.
 */

import { TOAST_EXIT_MS } from "@/lib/feel";
import { useEffect, useRef } from "react";
import { useHaptics } from "@/hooks/use_haptics";
import { prefersReducedMotion } from "@/lib/motion";
import { createPortal } from "react-dom";
import { cx } from "@/features/common/primitives";

export function UndoToast({
  message,
  actionLabel = "Undo",
  onUndo,
  onDismiss,
  busy = false,
  anchor = "bar",
}: {
  message: React.ReactNode;
  actionLabel?: string;
  onUndo: () => void;
  onDismiss: () => void;
  /** True while the reversal is in flight. */
  busy?: boolean;
  anchor?: "bar" | "action";
}): React.JSX.Element {
  const haptic = useHaptics();
  const host = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const node = host.current;
    return () => {
      if (!node || node.isConnected || prefersReducedMotion() || typeof node.animate !== "function") return;
      const copy = node.cloneNode(true) as HTMLElement;
      copy.removeAttribute("role"); copy.setAttribute("aria-hidden", "true"); copy.inert = true;
      copy.classList.remove("feel-toast"); document.body.append(copy);
      const animation = copy.animate([{ opacity: 1, translate: "0 0" }, { opacity: 0, translate: "0 var(--feel-travel)" }], { duration: TOAST_EXIT_MS, easing: "ease-out", fill: "forwards" });
      void animation.finished.then(() => copy.remove()).catch((cause: unknown) => { copy.remove(); console.warn("Toast exit interrupted:", cause); });
    };
  }, []);
  return createPortal(
    <div
      ref={host}
      role="status"
      className={cx(
        "feel-toast fixed inset-x-4 z-50 mx-auto flex max-w-md items-center justify-between gap-phi-0 rounded-lg bg-bg-3 px-phi-0 py-phi-n1 shadow-lg",
        anchor === "bar" ? "bottom-28 md:bottom-8" : "bottom-safe-action toast-action",
      )}
    >
      <span className={cx("min-w-0 text-phi-n1 text-tx", anchor === "action" && "flex items-center gap-phi-n6 overflow-hidden")}>{message}</span>
      <div className="flex shrink-0 items-center gap-phi-n6">
        <button
          type="button"
          onClick={() => { if (!busy) { haptic("tap"); onUndo(); } }}
          aria-busy={busy}
          className="min-h-[44px] px-phi-n3 text-phi-n1 font-bold text-acc aria-busy:opacity-60"
        >
          {actionLabel}
        </button>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="min-h-[44px] px-phi-n3 text-tx-2"
        >
          ✕
        </button>
      </div>
    </div>,
    document.body,
  );
}
